/**
 * Pure hast tree-transform used by LessonMarkdown's rehype pipeline.
 *
 * Deliberately has NO import of react-markdown/React so it stays unit-testable:
 * react-markdown v10's ESM-only dependency chain (unified/vfile/mdast-util-*)
 * can't be parsed by CRA's default Jest transform, so any test that
 * transitively imports LessonMarkdown.tsx crashes at import time (see
 * components/__mocks__/LessonMarkdown.tsx). Keeping the tree logic in its own
 * dependency-free module lets it be tested directly without touching the
 * shared CRA Jest config.
 */

// v2: after each segment whose anchor is in `anchors`, insert a
// <segment-practice anchor> placeholder node (segment END, so it renders as a
// practice card once the segment's content has been read).
// v3 (top-of-segment slides): immediately after a matching h2, also insert a
// <segment-slides anchor> placeholder (segment START, so the slide strip
// renders before the segment's body). Both placeholders share the same
// `wanted` anchor set. Runs AFTER rehype-slug so heading ids exist.
export function rehypeSegmentPractice(options: { anchors: string[] }) {
  const wanted = new Set(options.anchors || []);
  return (tree: any) => {
    if (wanted.size === 0) return;
    const out: any[] = [];
    let currentAnchor: string | null = null;
    const flush = () => {
      if (currentAnchor && wanted.has(currentAnchor)) {
        out.push({
          type: "element",
          tagName: "segment-practice",
          properties: { anchor: currentAnchor },
          children: [],
        });
      }
    };
    for (const node of tree.children) {
      if (node.type === "element" && node.tagName === "h2" && node.properties?.id) {
        flush(); // close the previous segment before this heading starts a new one
        currentAnchor = String(node.properties.id);
        out.push(node);
        if (wanted.has(currentAnchor)) {
          out.push({
            type: "element",
            tagName: "segment-slides",
            properties: { anchor: currentAnchor },
            children: [],
          });
        }
        continue;
      }
      out.push(node);
    }
    flush(); // close the final segment
    tree.children = out;
  };
}
